import { Link, navigate } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveContext } from '../../contexts/ResponsiveContext';
import { useCurrentPath } from '../../contexts/useCurrentPath';
import { useSiteMetadataVersions } from '../../queries/useSiteMetadataVersions';
import { DocOverview } from '../../types/docs';
import { getUrlForChangedVersion } from './sidebar.utils';
import documentationLogo from '../../images/domino-logo-large.png';
import { MainPath } from '../../types/types';
import { buildAppUrl } from '../../utils/buildAppUrl';
import { useLocation } from '../../contexts/useLocation';
import { useMemo } from 'react';
import TextTooltip from './components/TextTooltip';
import { compareVersions } from '../../utils/getIntegralVersion';
import { useVersionLookup } from '../../utils/getVersionLookup';
import { FaBars } from 'react-icons/fa';
import SidebarMenu from './components/SidebarMenu';
import ToggleNightMode from '../navbar/ToggleNightMode';
import { MenuItemType, MenuProps, SubMenuItemProps, MenuItemProps } from '../../types/menu';
import VersionPicker from './components/VersionPicker'; // Import the new VersionSelector component
import GuideScroller from './components/GuideScroller';
/* import { FiSearch } from 'react-icons/fi'; */

const getLinkWithPath = (doc: DocOverview, url: string) => (
  <Link
    key={doc.id}
    to={url}
    id={doc.id}
    className="text-bright-white text-sm font-medium pl-0 hover:text-vivid-orange w-full flex items-center min-h-6 p-1 h-auto"
  >
    {doc.sidebar || doc.title}
  </Link>
);

const getSubMenuItems = (props: SubMenuItemProps): any => {
  const { doc, latestVersion, version, currentPath, location } = props;
  const url = buildAppUrl({
    latestVersion,
    permalink: doc.permalink,
    slug: doc.slug,
    title: doc.title,
    version,
    path: currentPath,
  });
  if (doc.children.length === 0) {
    return {
      key: doc.id,
      label: getLinkWithPath(doc, url),
    };
  } else {
    return {
      key: doc.id,
      label: getLinkWithPath(doc, url),
      ...(doc.children.length > 0 && {
        children: getMenuItemChildren({
          doc,
          currentPath,
          version,
          latestVersion,
          location,
        }),
      }),
    };
  }
};

const getMenuItemChildren = (props: SubMenuItemProps): any[] =>
  props.doc.children.map((doc: DocOverview) => getSubMenuItems({ ...props, doc }));

const getMenuItems = (props: MenuItemProps): any[] => {
  const { docs, latestVersion, version, currentPath, location } = props;

  return docs.flatMap((doc, index) => {
    const items: any[] = []; // Create an array to hold items for this iteration

    if (doc['section']) {
      items.push({
        key: `section-${index}`,
        label: (
          <span className={`section-span ${doc.separator === 'true' ? 'border-t border-faint-border' : ''}`}>
            {doc.section}
          </span>
        ),
        className: 'section-header',
        disabled: true,
      }); // Add the section header to the items array
    }

    const item = getSubMenuItems({
      doc,
      currentPath,
      version,
      latestVersion,
      location,
    });

    // If there is no section but a separator, add a bottom border to the menu item
    if (!doc.section && doc.separator === 'true') {
      item.className = 'border-b border-faint-border';
    }

    items.push(item);
    return items;
  });
};

const Sidebar = (props: MenuProps) => {
  const { categoryVersions, version, docs, permalink, title } = props;
  const { dispatch } = useContext(ResponsiveContext);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const location = useLocation();
  const versionLookup = useVersionLookup(); // Move this up
  const currentPath = useCurrentPath();
  const { latestVersion } = useSiteMetadataVersions();

  function versionCompare(a: any, b: any) {
    if (!a?.label?.props || !b?.label?.props) return 0;
    const propsA = a.label.props;
    const propsB = b.label.props;

    // Safe navigation for path comparison
    const pathA = propsA?.to || '';
    const pathB = propsB?.to || '';

    if (pathA === '/release_notes/preview-features/') return -1;
    if (pathB === '/release_notes/preview-features/') return +1;

    // Only compare versions if both exist
    if (propsA?.version && propsB?.version) {
      return compareVersions(propsB.version, propsA.version);
    }
    return 0;
  }

  const items = useMemo(() => {
    return getMenuItems({
      docs,
      currentPath: currentPath as MainPath,
      version,
      latestVersion,
      location,
    });
  }, [docs, currentPath, version, latestVersion, location]);

  const getSortedItems = (items: MenuItemType[], currentPath: string | null): MenuItemType[] => {
    if (currentPath !== 'release_notes') return items;
    return [...items].sort(versionCompare);
  };

  const sortedItems: MenuItemType[] = useMemo(() => {
    return getSortedItems(items, currentPath);
  }, [items, currentPath]);

  const changeSelectedVersion = (version: string) => {
    if (currentPath == null) return;
    if (currentPath === 'release_notes') return;
    const url = getUrlForChangedVersion({
      latestVersion: latestVersion,
      version: version,
      title: title,
      currentPath: currentPath,
      permalink: permalink,
      versionLookup: versionLookup,
    });
    navigate(url);
  };

  /** Recursively finds the selected key and its parent keys */
  const findOpenKeys = (doc: DocOverview, parentKeys: string[] = []): string[] | null => {
    const url = buildAppUrl({
      latestVersion,
      permalink: doc.permalink,
      slug: doc.slug,
      title: doc.title,
      version,
      path: currentPath as MainPath,
    });

    if (location.pathname === url) {
      return [...parentKeys, doc.id]; // Open all parent keys up to this doc
    }

    for (const child of doc.children) {
      const result = findOpenKeys(child, [...parentKeys, doc.id]);
      if (result) return result;
    }

    return null;
  };

  // This is a fix for no global state to check guides
  // This resets the selected menu items when the guide changes
  useEffect(() => {
    const pathNameArray = location.pathname.split('/');
    const guideFromPath = pathNameArray[pathNameArray.length - 2];
    if (guideFromPath == 'admin-guide' || guideFromPath == 'api-guide' || guideFromPath == 'user-guide') {
      setOpenKeys([]);
      /* setSelectedKey(""); */
    }
  }, [currentPath]);

  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.length > 0) return prev;

      for (const doc of docs) {
        const result = findOpenKeys(doc);
        if (result) return result;
      }

      return prev;
    });

    setOpenKeys((prev) => {
      if (prev.length > 0) return prev;

      for (const doc of docs) {
        const result = findOpenKeys(doc);
        if (result) return result;
      }

      return prev;
    });
  }, [version, title]);

  const handleMenuAction = (key: string, keys: string[], selected: boolean) => {
    if (selected) {
      setSelectedKeys(keys);

      if (!openKeys.includes(key)) {
        setOpenKeys((prev) => [...prev, key]);
      }
    } else {
      setOpenKeys(
        (prev) =>
          prev.includes(key)
            ? prev.filter((k) => k !== key) // Close if open
            : [...prev, key] // Open if closed
      );
    }
  };

  return (
    <>
      {/* Hamburger Menu (Visible on Mobile) */}

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex md:hidden fixed mt-0.5 top-3 right-4 w-8 h-8 text-very-dark-grey items-center justify-center dark:text-bright-white text-2xl bg-bright-white dark:bg-very-dark-grey border border-medium-dark-grey cursor-pointer z-40 rounded"
      >
        <FaBars className="w-6 h-4" />
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed md:sticky 
          top-0 left-0 bottom-0 
          bg-very-dark-grey 
          h-screen
          transition-all duration-300 
          z-40
          ${collapsed ? '-translate-x-full md:translate-x-0' : 'translate-x-0'}
          ${collapsed ? 'w-0 md:w-80' : 'w-full md:w-80'}
          ${collapsed ? 'overflow-hidden md:overflow-visible' : 'overflow-visible'}
        `}
      >
        {/* Close button for mobile */}

        <div id="leftnav" className="h-full flex flex-col overflow-y-auto sidebar-leftnav">
          <div className="flex flex-row items-center h-10 ml-8 pr-4 mt-3 mb-4 ">
            <div className="flex-grow justify-start">
              <Link className="w-28" to="/" onClick={() => setCollapsed(true)}>
                <img
                  loading="lazy"
                  src={documentationLogo}
                  className="cursor-pointer w-28 h-5 m-0"
                  alt="domino logo"
                  id="domino-logo"
                />
              </Link>
            </div>
            <div className="w-12 flex flex-grow md:hidden justify-end">
              <ToggleNightMode
                visible={collapsed ? false : true}
                darkBackground="bg-medium-dark-grey"
                lightBackground="bg-medium-dark-grey"
                sunStroke="stroke-bright-white"
                moonStroke="stroke-bright-white"
                lightDotColour="after:bg-bright-white"
                darkDotColour=""
              />
            </div>
          {/*   <div className="w-8 flex justify-end mx-1">
              <button
                onClick={() => setCollapsed(true)}
                className="md:hidden w-8 h-8 text-white bg-transparent border border-medium-dark-grey cursor-pointer z-50 rounded flex items-center justify-center"
              >
                <FiSearch className="w-4 h-4" />
              </button>
            </div> */}
            <div className="w-8 flex justify-end">
              <button
                onClick={() => setCollapsed(true)}
                className="md:hidden w-8 h-8 text-white text-2xl bg-transparent border border-medium-dark-grey cursor-pointer z-50 rounded"
              >
                &times;
              </button>
            </div>
          </div>
          <hr className="border-medium-dark-grey w-full" />

          {currentPath && currentPath !== 'release_notes' && (
            <>
              <VersionPicker
                currentPath={currentPath}
                version={version}
                categoryVersions={categoryVersions}
                latestVersion={latestVersion}
                changeSelectedVersion={changeSelectedVersion}
                dispatch={dispatch}
              />
            </>
          )}

          <SidebarMenu
            sortedItems={sortedItems}
            openKeys={openKeys}
            selectedKeys={selectedKeys}
            handleMenuAction={handleMenuAction}
          />
          {currentPath && currentPath == 'release_notes' && (
            <div className="flex flex-col items-center align-middle pt-4 pl-8 pr-8">
              <TextTooltip
                text="Older versions"
                tooltipText="Older release notes can be found on our archive site"
                link="https://archive.docs.dominodatalab.com/release_notes"
              />
            </div>
          )}
          <GuideScroller />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
